import React, { useEffect, useMemo, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import InputGroup from 'react-bootstrap/InputGroup';
import { useTranslation } from '../i18n';

type BirthDateField = 'day' | 'month' | 'year';

export interface BirthDateValue {
  day?: string | number | null;
  month?: string | number | null;
  year?: string | number | null;
}

export type BirthDateInput = BirthDateValue | string | null | undefined;

export interface BirthDateParts {
  day: string;
  month: string;
  year: string;
}

export interface BirthDateChangePayload {
  formatted: string;
  parts: BirthDateParts;
  isComplete: boolean;
}

export interface BirthDatePickerProps {
  value?: BirthDateInput;
  onChange: (change: BirthDateChangePayload) => void;
  label?: React.ReactNode;
  className?: string;
  controlIdPrefix?: string;
  required?: boolean;
  yearCount?: number;
  youngestAge?: number;
  dayPlaceholder?: string;
  monthPlaceholder?: string;
  yearPlaceholder?: string;
  variant?: 'floating' | 'input-group';
  prefix?: React.ReactNode;
}

interface MonthConfig {
  value: string;
  label: string;
  aliases: string[];
}

const MONTHS: MonthConfig[] = [
  { value: '1', label: 'January', aliases: ['jan', 'january'] },
  { value: '2', label: 'February', aliases: ['feb', 'february'] },
  { value: '3', label: 'March', aliases: ['mar', 'march'] },
  { value: '4', label: 'April', aliases: ['apr', 'april'] },
  { value: '5', label: 'May', aliases: ['may'] },
  { value: '6', label: 'June', aliases: ['jun', 'june'] },
  { value: '7', label: 'July', aliases: ['jul', 'july'] },
  { value: '8', label: 'August', aliases: ['aug', 'august'] },
  { value: '9', label: 'September', aliases: ['sep', 'sept', 'september'] },
  { value: '10', label: 'October', aliases: ['oct', 'october'] },
  { value: '11', label: 'November', aliases: ['nov', 'november'] },
  { value: '12', label: 'December', aliases: ['dec', 'december'] },
];

const monthAliasMap: Map<string, string> = (() => {
  const map = new Map<string, string>();

  for (const month of MONTHS) {
    const normalizedValue = month.value;
    map.set(normalizedValue, normalizedValue);
    map.set(normalizedValue.padStart(2, '0'), normalizedValue);
    map.set(month.label.toLowerCase(), normalizedValue);

    for (const alias of month.aliases) {
      map.set(alias.toLowerCase(), normalizedValue);
    }
  }

  return map;
})();

const EMPTY_PARTS: BirthDateParts = { day: '', month: '', year: '' };

const isLeapYear = (year: number): boolean => {
  if (!Number.isFinite(year)) {
    return false;
  }

  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

const getDaysInMonth = (month?: number, year?: number): number => {
  const normalizedMonth = Number(month);

  if (!Number.isFinite(normalizedMonth) || normalizedMonth <= 0) {
    return 31;
  }

  if (normalizedMonth === 2) {
    return isLeapYear(Number(year)) ? 29 : 28;
  }

  if ([4, 6, 9, 11].includes(normalizedMonth)) {
    return 30;
  }

  if ([1, 3, 5, 7, 8, 10, 12].includes(normalizedMonth)) {
    return 31;
  }

  return 31;
};

const normalizeDay = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '';
  }

  const numberValue = Number.parseInt(String(value).trim(), 10);

  if (!Number.isFinite(numberValue) || numberValue < 1 || numberValue > 31) {
    return '';
  }

  return String(numberValue);
};

const normalizeYear = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '';
  }

  const numberValue = Number.parseInt(String(value).trim(), 10);

  if (!Number.isFinite(numberValue) || numberValue <= 0) {
    return '';
  }

  return String(numberValue);
};

const normalizeMonth = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value >= 1 && value <= 12 ? String(value) : '';
  }

  const raw = String(value).trim();

  if (!raw) {
    return '';
  }

  const numeric = Number.parseInt(raw, 10);

  if (Number.isFinite(numeric) && numeric >= 1 && numeric <= 12) {
    return String(numeric);
  }

  const mapped = monthAliasMap.get(raw.toLowerCase());

  return mapped ?? '';
};

const clampDay = (parts: BirthDateParts): BirthDateParts => {
  const monthNumber = Number.parseInt(parts.month, 10);
  const yearNumber = Number.parseInt(parts.year, 10);
  const maxDay = getDaysInMonth(monthNumber, yearNumber);
  const dayNumber = Number.parseInt(parts.day, 10);

  if (!parts.day) {
    return parts;
  }

  if (!Number.isFinite(dayNumber) || dayNumber < 1) {
    return { ...parts, day: '' };
  }

  if (dayNumber > maxDay) {
    return { ...parts, day: String(maxDay) };
  }

  return parts;
};

const splitDateTokens = (input: string): string[] => {
  return input
    .split(/[\-\/.,\s]+/)
    .map((token) => token.trim())
    .filter(Boolean);
};

const parseTokensToParts = (tokens: string[]): BirthDateParts => {
  if (tokens.length === 0) {
    return { ...EMPTY_PARTS };
  }

  const [firstToken = '', secondToken = '', thirdToken = ''] = tokens;
  const firstMonth = normalizeMonth(firstToken);
  const secondMonth = normalizeMonth(secondToken);
  const thirdMonth = normalizeMonth(thirdToken);

  if (firstMonth && !secondMonth) {
    return clampDay({
      day: normalizeDay(secondToken),
      month: firstMonth,
      year: normalizeYear(thirdToken),
    });
  }

  if (!firstMonth && secondMonth) {
    return clampDay({
      day: normalizeDay(firstToken),
      month: secondMonth,
      year: normalizeYear(thirdToken),
    });
  }

  if (!firstMonth && !secondMonth && thirdMonth) {
    return clampDay({
      day: normalizeDay(secondToken),
      month: thirdMonth,
      year: normalizeYear(firstToken),
    });
  }

  const firstIsYear = firstToken.length === 4 && Number.isFinite(Number(firstToken));
  const thirdIsYear = thirdToken.length === 4 && Number.isFinite(Number(thirdToken));

  if (firstIsYear && !thirdIsYear) {
    return clampDay({
      day: normalizeDay(thirdToken),
      month: normalizeMonth(secondToken),
      year: normalizeYear(firstToken),
    });
  }

  if (thirdIsYear) {
    return clampDay({
      day: normalizeDay(firstToken),
      month: normalizeMonth(secondToken),
      year: normalizeYear(thirdToken),
    });
  }

  return clampDay({
    day: normalizeDay(firstToken),
    month: normalizeMonth(secondToken),
    year: normalizeYear(thirdToken),
  });
};

export const parseBirthDateInput = (input: BirthDateInput): BirthDateParts => {
  if (!input) {
    return { ...EMPTY_PARTS };
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();

    if (!trimmed) {
      return { ...EMPTY_PARTS };
    }

    return parseTokensToParts(splitDateTokens(trimmed));
  }

  return clampDay({
    day: normalizeDay(input.day ?? ''),
    month: normalizeMonth(input.month ?? ''),
    year: normalizeYear(input.year ?? ''),
  });
};

const padTwo = (value: string): string => value.padStart(2, '0');

export const formatBirthDateParts = (parts: BirthDateParts): string => {
  if (!parts.day || !parts.month || !parts.year) {
    return '';
  }

  return `${padTwo(parts.day)}-${padTwo(parts.month)}-${parts.year}`;
};

const partsEqual = (a: BirthDateParts, b: BirthDateParts): boolean => {
  return a.day === b.day && a.month === b.month && a.year === b.year;
};

const BirthDatePicker: React.FC<BirthDatePickerProps> = ({
  value,
  onChange,
  label,
  className = '',
  controlIdPrefix = 'birth-date-picker',
  required = false,
  yearCount = 100,
  youngestAge = 18,
  dayPlaceholder,
  monthPlaceholder,
  yearPlaceholder,
  variant = 'floating',
  prefix,
}) => {
  const { t } = useTranslation();
  const [parts, setParts] = useState<BirthDateParts>(() => parseBirthDateInput(value));

  // Use translated defaults if not provided
  const finalDayPlaceholder = dayPlaceholder || t('auth.day');
  const finalMonthPlaceholder = monthPlaceholder || t('auth.month');
  const finalYearPlaceholder = yearPlaceholder || t('auth.year');

  // Translated months
  const translatedMonths: MonthConfig[] = useMemo(() => [
    { value: '1', label: t('auth.january'), aliases: ['jan', 'january'] },
    { value: '2', label: t('auth.february'), aliases: ['feb', 'february'] },
    { value: '3', label: t('auth.march'), aliases: ['mar', 'march'] },
    { value: '4', label: t('auth.april'), aliases: ['apr', 'april'] },
    { value: '5', label: t('auth.may'), aliases: ['may'] },
    { value: '6', label: t('auth.june'), aliases: ['jun', 'june'] },
    { value: '7', label: t('auth.july'), aliases: ['jul', 'july'] },
    { value: '8', label: t('auth.august'), aliases: ['aug', 'august'] },
    { value: '9', label: t('auth.september'), aliases: ['sep', 'sept', 'september'] },
    { value: '10', label: t('auth.october'), aliases: ['oct', 'october'] },
    { value: '11', label: t('auth.november'), aliases: ['nov', 'november'] },
    { value: '12', label: t('auth.december'), aliases: ['dec', 'december'] },
  ], [t]);

  const externalParts = useMemo(() => parseBirthDateInput(value), [value]);

  useEffect(() => {
    if (!partsEqual(parts, externalParts)) {
      setParts(externalParts);
    }
  }, [externalParts, parts]);

  const currentYear = new Date().getFullYear();
  const startYear = currentYear - youngestAge;

  const years = useMemo(
    () => Array.from({ length: yearCount }, (_, index) => startYear - index),
    [startYear, yearCount]
  );

  const maxDays = useMemo(() => {
    const parsedMonth = Number.parseInt(parts.month, 10);
    const parsedYear = Number.parseInt(parts.year, 10);
    return getDaysInMonth(parsedMonth, parsedYear);
  }, [parts.month, parts.year]);

  const days = useMemo(
    () => Array.from({ length: maxDays }, (_, index) => index + 1),
    [maxDays]
  );

  const emitChange = (nextParts: BirthDateParts) => {
    setParts(nextParts);

    const formatted = formatBirthDateParts(nextParts);
    const isComplete = Boolean(nextParts.day && nextParts.month && nextParts.year);

    onChange({
      formatted,
      parts: nextParts,
      isComplete,
    });
  };

  const handleFieldChange = (field: BirthDateField) => (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const raw = event.target.value;
    const nextParts: BirthDateParts = { ...parts };

    if (field === 'day') {
      nextParts.day = normalizeDay(raw);
    } else if (field === 'month') {
      nextParts.month = normalizeMonth(raw);
    } else {
      nextParts.year = normalizeYear(raw);
    }

    const limit = getDaysInMonth(
      Number.parseInt(nextParts.month, 10),
      Number.parseInt(nextParts.year, 10)
    );

    if (nextParts.day) {
      const parsedDay = Number.parseInt(nextParts.day, 10);
      if (Number.isFinite(parsedDay) && parsedDay > limit) {
        nextParts.day = String(limit);
      }
    }

    emitChange(nextParts);
  };

  const renderDaySelect = (extraClassName?: string) => (
    <Form.Select
      name="day"
      value={parts.day}
      onChange={handleFieldChange('day')}
      required={required}
      className={extraClassName}
    >
      <option value="">{finalDayPlaceholder}</option>
      {days.map((value) => (
        <option key={value} value={value}>
          {value}
        </option>
      ))}
    </Form.Select>
  );

  const renderMonthSelect = (extraClassName?: string) => (
    <Form.Select
      name="month"
      value={parts.month}
      onChange={handleFieldChange('month')}
      required={required}
      className={extraClassName}
    >
      <option value="">{finalMonthPlaceholder}</option>
      {translatedMonths.map((month) => (
        <option key={month.value} value={month.value}>
          {month.label}
        </option>
      ))}
    </Form.Select>
  );

  const renderYearSelect = (extraClassName?: string) => (
    <Form.Select
      name="year"
      value={parts.year}
      onChange={handleFieldChange('year')}
      required={required}
      className={extraClassName}
    >
      <option value="">{finalYearPlaceholder}</option>
      {years.map((value) => (
        <option key={value} value={value}>
          {value}
        </option>
      ))}
    </Form.Select>
  );

  const containerClassName = ['birth-date-picker', className].filter(Boolean).join(' ');

  return (
    <Form.Group className={containerClassName}>
      {label && (
        <Form.Label className="fw-semibold">{label}</Form.Label>
      )}
      {variant === 'input-group' ? (
        <InputGroup className="date-selector">
          {prefix && (
            <InputGroup.Text>
              {prefix}
            </InputGroup.Text>
          )}
          {renderDaySelect()}
          {renderMonthSelect()}
          {renderYearSelect()}
        </InputGroup>
      ) : (
        <Row className="g-2">
          <Col xs={4}>
            <FloatingLabel
              controlId={`${controlIdPrefix}-day`}
              label={finalDayPlaceholder}
            >
              {renderDaySelect()}
            </FloatingLabel>
          </Col>
          <Col xs={4}>
            <FloatingLabel
              controlId={`${controlIdPrefix}-month`}
              label={finalMonthPlaceholder}
            >
              {renderMonthSelect()}
            </FloatingLabel>
          </Col>
          <Col xs={4}>
            <FloatingLabel
              controlId={`${controlIdPrefix}-year`}
              label={finalYearPlaceholder}
            >
              {renderYearSelect()}
            </FloatingLabel>
          </Col>
        </Row>
      )}
    </Form.Group>
  );
};

export default BirthDatePicker;
